import React, { useState, useEffect, useRef } from 'react'
import { Col, Row, Table } from 'reactstrap'
import { Controller, useForm } from 'react-hook-form'
import { SearchableSelectHookForm, TextFieldHookForm, NumberFieldHookForm } from '../../../../layout/HookFormInputs'
import Button from '../../../../layout/Button'
import TooltipCustom from '../../../../common/Tooltip'
import NoContentFound from '../../../../common/NoContentFound'
import Toaster from '../../../../common/Toaster'
import { checkForDecimalAndNull, checkForNull, checkWhiteSpaces, number, integerAndDecimalNumberLimit, blockInvalidNumberKeys } from '../../../../../helper'
import { EMPTY_DATA } from '../../../../../config/constants'

const FORM_DEFAULTS = {
    mode: 'onChange',
    reValidateMode: 'onChange'
}

function ColourChangeCost({
    ViewMode,
    CostingViewMode,
    IsLocked,
    IsLockTabInCBCCostingForCustomerRFQ,
    rawMaterialOptions,
    NoOfDecimalForInputOutput,
    NoOfDecimalForPrice,
    onTotalCostChange,
    initialColourChangeRawMaterials,
    onColourChangeRawMaterialsChange
}) {
    // Colour Change Cost State - flat array of raw materials and total
    const [rawMaterials, setRawMaterials] = useState([])
    const [totalCostForColorChange, setTotalCostForColorChange] = useState(0)
    const isInitializedRef = useRef(false)

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
        reset
    } = useForm(FORM_DEFAULTS)

    // Initialize data from API - ColourChangeRawMaterials is a flat array
    // Only runs ONCE on initial load to prevent overwriting user input
    useEffect(() => {
        if (isInitializedRef.current) return // Already initialized, skip
        if (initialColourChangeRawMaterials && initialColourChangeRawMaterials.length > 0) {
            isInitializedRef.current = true // Mark as initialized

            // Map API data to internal format
            const mappedData = initialColourChangeRawMaterials.map(rm => ({
                RawMaterialId: rm?.RawMaterialId,
                RawMaterial: rm?.RawMaterialName || rm?.RawMaterial,
                UOMId: rm?.UOMId,
                UOM: rm?.UOM,
                UOMSymbol: rm?.UOMSymbol,
                NetLandedCost: rm?.RMRate || rm?.NetLandedCost,
                ConsumptionInLtr: rm?.ConsumptionInLtr,
                Cost: rm?.Cost
            }))

            // Calculate total cost
            const totalCost = mappedData.reduce((total, rm) => total + checkForNull(rm?.Cost), 0)

            setRawMaterials(mappedData)
            setTotalCostForColorChange(totalCost)

            // Set form values for table inputs - ONLY on initial load
            setTimeout(() => {
                mappedData.forEach((rm, index) => {
                    const fieldKey = `${rm?.RawMaterialId}${rm?.RawMaterial}${index}`
                    setValue(`ConsumptionInLtr${fieldKey}`, rm.ConsumptionInLtr ?? '')
                    setValue(`Cost${fieldKey}`, rm?.Cost ? checkForDecimalAndNull(rm?.Cost, NoOfDecimalForPrice) : '')
                })
            }, 100)

            // Notify parent of total cost
            if (onTotalCostChange) {
                onTotalCostChange(totalCost)
            }
        }
    }, [initialColourChangeRawMaterials])

    // Add raw materials to table
    const addColourChangeData = (data) => {
        if (!data?.ColourChangeRawMaterial?.length) return

        // Check for duplicates - filter out already existing raw materials
        const existingIds = rawMaterials.map(rm => rm?.RawMaterialId)
        const duplicates = data?.ColourChangeRawMaterial.filter(item => existingIds.includes(item?.RawMaterialId))
        const newItems = data?.ColourChangeRawMaterial.filter(item => !existingIds.includes(item?.RawMaterialId))

        if (duplicates.length > 0) {
            const duplicateNames = duplicates.map(d => d?.RawMaterial).join(', ')
            Toaster.warning(`Raw Material already exists: ${duplicateNames}`)
        }

        if (newItems.length === 0) {
            reset({ ColourChangeRawMaterial: null })
            return
        }

        // Clear form fields for new items
        newItems.forEach((item, idx) => {
            const fieldKey = `${item?.RawMaterialId}${item?.RawMaterial}${rawMaterials.length + idx}`
            setValue(`ConsumptionInLtr${fieldKey}`, '')
            setValue(`Cost${fieldKey}`, '')
        })

        const newRawMaterials = newItems.map(item => ({
            RawMaterialId: item?.RawMaterialId,
            RawMaterial: item?.RawMaterial,
            UOMId: item?.UOMId,
            UOM: item?.UOM,
            UOMSymbol: item?.UOMSymbol,
            NetLandedCost: item?.NetLandedCost,
            ConsumptionInLtr: 0,
            Cost: 0
        }))

        const updatedRawMaterials = [...rawMaterials, ...newRawMaterials]
        setRawMaterials(updatedRawMaterials)
        calculateTotalCost(updatedRawMaterials)

        reset({ ColourChangeRawMaterial: null })
    }

    // Delete raw material row
    const deleteRawMaterial = (indexToDelete) => {
        // Clear all form fields first
        rawMaterials.forEach((rm, index) => {
            const fieldKey = `${rm?.RawMaterialId}${rm?.RawMaterial}${index}`
            setValue(`ConsumptionInLtr${fieldKey}`, '')
            setValue(`Cost${fieldKey}`, '')
        })

        const updatedRawMaterials = rawMaterials.filter((_, i) => i !== indexToDelete)
        setRawMaterials(updatedRawMaterials)

        // Re-set form values with new indices
        setTimeout(() => {
            updatedRawMaterials.forEach((rm, index) => {
                const fieldKey = `${rm?.RawMaterialId}${rm?.RawMaterial}${index}`
                setValue(`ConsumptionInLtr${fieldKey}`, rm?.ConsumptionInLtr ? checkForDecimalAndNull(rm?.ConsumptionInLtr, NoOfDecimalForInputOutput) : '')
                setValue(`Cost${fieldKey}`, rm?.Cost ? checkForDecimalAndNull(rm?.Cost, NoOfDecimalForPrice) : '')
            })
        }, 50)

        calculateTotalCost(updatedRawMaterials)
    }

    // Calculate row values when consumption changes
    const calculateRowValues = (consumptionInLtr, index, rm) => {
        const cost = checkForNull(consumptionInLtr) * checkForNull(rm?.NetLandedCost)

        // Use functional update to avoid stale state
        setRawMaterials(prev => {
            const updatedRawMaterials = [...prev]
            if (updatedRawMaterials[index]) {
                updatedRawMaterials[index] = {
                    ...updatedRawMaterials[index],
                    ConsumptionInLtr: checkForNull(consumptionInLtr),
                    Cost: checkForNull(cost)
                }
            }
            // Calculate total with updated data
            calculateTotalCost(updatedRawMaterials)
            return updatedRawMaterials
        })

        // Only update the Cost field (read-only), NOT the ConsumptionInLtr field
        const fieldKey = `${rm?.RawMaterialId}${rm?.RawMaterial}${index}`
        setValue(`Cost${fieldKey}`, checkForDecimalAndNull(cost, NoOfDecimalForPrice))
    }

    // Calculate total cost and notify parent
    const calculateTotalCost = (rawMaterialsData) => {
        const total = rawMaterialsData.reduce((sum, rm) => sum + checkForNull(rm?.Cost || 0), 0)

        setTotalCostForColorChange(total)

        // Notify parent of total cost change
        if (onTotalCostChange) {
            onTotalCostChange(total)
        }

        // Convert to API format and notify parent
        if (onColourChangeRawMaterialsChange) {
            const apiData = rawMaterialsData.map((rm, index) => ({
                RawMaterialId: rm?.RawMaterialId,
                RawMaterialName: rm?.RawMaterial,
                UOMId: rm?.UOMId,
                UOM: rm?.UOM,
                UOMSymbol: rm?.UOMSymbol,
                RMRate: rm?.NetLandedCost,
                ConsumptionInLtr: rm?.ConsumptionInLtr,
                Cost: rm?.Cost,
                RawMaterialSequence: index + 1
            }))
            onColourChangeRawMaterialsChange(apiData)
        }
    }

    return (
        <div className="colour-change-cost-section mt-4">
            <Row className="drawer-heading mb-2">
                <Col md="12">
                    <div className="header-title">
                        <h5>Colour Change Cost:</h5>
                    </div>
                </Col>
            </Row>

            {!ViewMode && !IsLocked && <Row>
                <Col md="4">
                    <SearchableSelectHookForm
                        label="Raw Material"
                        name="ColourChangeRawMaterial"
                        placeholder="Select"
                        Controller={Controller}
                        control={control}
                        register={register}
                        mandatory
                        rules={{ required: true }}
                        options={rawMaterialOptions}
                        isMulti
                        handleChange={() => { }}
                        defaultValue=""
                        customClassName="withBorder"
                        errors={errors.ColourChangeRawMaterial}
                        disabled={ViewMode || CostingViewMode || IsLockTabInCBCCostingForCustomerRFQ}
                    />
                </Col>
                <Col md="8">
                    <div className="d-flex mt-4 pt-2">
                        <Button
                            id="ColourChangeCost_add"
                            type="button"
                            className="mr5 mb-2"
                            icon="plus"
                            buttonName="Add"
                            disabled={CostingViewMode || IsLocked || IsLockTabInCBCCostingForCustomerRFQ}
                            onClick={handleSubmit(addColourChangeData)}
                        />
                    </div>
                </Col>
            </Row>}

            {/* Colour Change Cost Table */}
            <Table responsive bordered className="table-with-input-data">
                <thead>
                    <tr>
                        <th width="120">Raw Material</th>
                        <th width="120">UOM</th>
                        <th width="120">RM Rate</th>
                        <th width="120">Consumption in Ltr.</th>
                        <th width="120">Cost</th>
                        {!ViewMode && !IsLocked && <th width="80">Action</th>}
                    </tr>
                </thead>
                <tbody>
                    {!rawMaterials?.length ? (
                        <tr>
                            <td colSpan={!ViewMode && !IsLocked ? 6 : 5}>
                                <NoContentFound title={EMPTY_DATA} />
                            </td>
                        </tr>
                    ) : (
                        rawMaterials.map((rm, index) => (
                            <tr key={`colour-rm-${index}`}>
                                <td>{rm?.RawMaterial || '-'}</td>
                                <td>{rm?.UOM || '-'}</td>
                                <td>{checkForDecimalAndNull(rm?.NetLandedCost, NoOfDecimalForInputOutput) || ''}</td>
                                <td>
                                    <NumberFieldHookForm
                                        label={false}
                                        id={`ConsumptionInLtr${rm?.RawMaterialId}${index}`}
                                        name={`ConsumptionInLtr${rm?.RawMaterialId}${rm?.RawMaterial}${index}`}
                                        Controller={Controller}
                                        control={control}
                                        register={register}
                                        rules={{
                                            validate: { number, checkWhiteSpaces, integerAndDecimalNumberLimit }
                                        }}
                                        onKeyDown={blockInvalidNumberKeys}
                                        handleChange={(e) => calculateRowValues(e.target.value, index, rm)}
                                        defaultValue=""
                                        customClassName="withBorder mb-0 paint-and-masking"
                                        errors={errors[`ConsumptionInLtr${rm?.RawMaterialId}${rm?.RawMaterial}${index}`]}
                                        disabled={ViewMode || CostingViewMode || IsLocked || IsLockTabInCBCCostingForCustomerRFQ}
                                        hoverTitle={rm?.ConsumptionInLtr}
                                    />
                                </td>
                                <td>
                                    <TooltipCustom
                                        id={`Cost${rm?.RawMaterialId}${index}`}
                                        disabledIcon
                                        tooltipText="Cost = Consumption in Ltr × RM Rate"
                                    />
                                    <TextFieldHookForm
                                        label={false}
                                        id={`Cost${rm?.RawMaterialId}${index}`}
                                        name={`Cost${rm?.RawMaterialId}${rm?.RawMaterial}${index}`}
                                        Controller={Controller}
                                        control={control}
                                        register={register}
                                        handleChange={() => { }}
                                        defaultValue=""
                                        customClassName="withBorder mb-0 paint-and-masking"
                                        errors={errors[`Cost${rm?.RawMaterialId}${rm?.RawMaterial}${index}`]}
                                        disabled
                                        hoverTitle={rm?.Cost}
                                    />
                                </td>
                                {!ViewMode && !IsLocked && (
                                    <td>
                                        <Button
                                            id={`ColourChangeCost_delete${index}`}
                                            className="mr-1"
                                            variant="Delete"
                                            disabled={ViewMode || CostingViewMode || IsLockTabInCBCCostingForCustomerRFQ}
                                            onClick={() => deleteRawMaterial(index)}
                                            title="Delete"
                                        />
                                    </td>
                                )}
                            </tr>
                        ))
                    )}
                    {/* Total Row */}
                    {rawMaterials?.length > 0 && (
                        <tr className="table-footer">
                            <th colSpan={4} className="text-right">Total Cost for Color Change</th>
                            <td colSpan={!ViewMode && !IsLocked ? 2 : 1}>
                                {checkForDecimalAndNull(totalCostForColorChange, NoOfDecimalForPrice)}
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        </div>
    )
}

export default ColourChangeCost
